import React, { useState } from 'react';
import { BarChart3Icon, DownloadIcon, PrinterIcon, MailIcon, FilterIcon, CalendarIcon, FileTextIcon } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { districts, deedTypes, landTypes } from '../data/seedData';
type ReportType = 'land-summary' | 'district-wise' | 'deed-wise' | 'litigation' | 'cost-summary';
export function Reports() {
  const {
    state
  } = useApp();
  const {
    language,
    landRecords
  } = state;
  const [reportType, setReportType] = useState<ReportType>('land-summary');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const handleGenerate = () => {
    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
      setShowPreview(true);
    }, 1500);
  };
  const getReportTitle = () => {
    switch (reportType) {
      case 'land-summary':
        return 'Land Record Summary';
      case 'district-wise':
        return 'District-wise Land Distribution';
      case 'deed-wise':
        return 'Deed Type Analysis';
      case 'litigation':
        return 'Litigation Status Report';
      case 'cost-summary':
        return 'Registration Cost Summary';
      default:
        return 'Report';
    }
  };
  const renderPreviewContent = () => {
    // Mock data based on report type
    switch (reportType) {
      case 'land-summary':
        return <div className="overflow-x-auto" data-id="element-1038">
            <table className="w-full text-sm" data-id="element-1039">
              <thead data-id="element-1040">
                <tr className="bg-gray-50 border-b border-gray-200" data-id="element-1041">
                  <th className="text-left px-4 py-3 font-medium text-gray-600" data-id="element-1042">
                    Category
                  </th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600" data-id="element-1043">
                    Count
                  </th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600" data-id="element-1044">
                    Total Area (Dec)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-1045">
                <tr data-id="element-1046">
                  <td className="px-4 py-3" data-id="element-1047">Active Records</td>
                  <td className="px-4 py-3 text-right" data-id="element-1048">
                    {landRecords.filter(r => r.status === 'Active').length}
                  </td>
                  <td className="px-4 py-3 text-right" data-id="element-1049">
                    {landRecords.filter(r => r.status === 'Active').reduce((sum, r) => sum + (r.areaDecimal || 0), 0)}
                  </td>
                </tr>
                <tr data-id="element-1050">
                  <td className="px-4 py-3" data-id="element-1051">Inactive Records</td>
                  <td className="px-4 py-3 text-right" data-id="element-1052">
                    {landRecords.filter(r => r.status === 'Inactive').length}
                  </td>
                  <td className="px-4 py-3 text-right" data-id="element-1053">
                    {landRecords.filter(r => r.status === 'Inactive').reduce((sum, r) => sum + (r.areaDecimal || 0), 0)}
                  </td>
                </tr>
                <tr className="bg-gray-50 font-medium" data-id="element-1054">
                  <td className="px-4 py-3" data-id="element-1055">Total</td>
                  <td className="px-4 py-3 text-right" data-id="element-1056">{landRecords.length}</td>
                  <td className="px-4 py-3 text-right" data-id="element-1057">
                    {landRecords.reduce((sum, r) => sum + (r.areaDecimal || 0), 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>;
      case 'cost-summary':
        const totalCost = landRecords.reduce((sum, r) => sum + (r.registrationCosts?.total || 0), 0);
        return <div className="overflow-x-auto" data-id="element-1058">
            <table className="w-full text-sm" data-id="element-1059">
              <thead data-id="element-1060">
                <tr className="bg-gray-50 border-b border-gray-200" data-id="element-1061">
                  <th className="text-left px-4 py-3 font-medium text-gray-600" data-id="element-1062">
                    Land ID
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600" data-id="element-1063">
                    District
                  </th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600" data-id="element-1064">
                    Deed Value
                  </th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600" data-id="element-1065">
                    Reg. Cost
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-1066">
                {landRecords.slice(0, 5).map(r => <tr key={r.id} data-id="element-1067">
                    <td className="px-4 py-3" data-id="element-1068">{r.landId}</td>
                    <td className="px-4 py-3" data-id="element-1069">{r.district}</td>
                    <td className="px-4 py-3 text-right" data-id="element-1070">
                      ৳ {r.deedValue?.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-1071">
                      ৳ {r.registrationCosts?.total?.toLocaleString()}
                    </td>
                  </tr>)}
                <tr className="bg-gray-50 font-medium" data-id="element-1072">
                  <td className="px-4 py-3" colSpan={3} data-id="element-1073">
                    Total Registration Costs
                  </td>
                  <td className="px-4 py-3 text-right" data-id="element-1074">
                    ৳ {totalCost.toLocaleString()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>;
      default:
        return <div className="p-8 text-center text-gray-500" data-id="element-1075">
            <p data-id="element-1076">Preview data for {getReportTitle()}</p>
          </div>;
    }
  };
  return <div className="p-4 lg:p-6 space-y-6" data-id="element-1077">
      {/* Header */}
      <div data-id="element-1078">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1079">
          {language === 'en' ? 'Reports' : 'রিপোর্ট'}
        </h1>
        <p className="text-gray-600 mt-1" data-id="element-1080">
          {language === 'en' ? 'Generate and download reports' : 'রিপোর্ট তৈরি এবং ডাউনলোড করুন'}
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6" data-id="element-1081">
        {/* Report Builder */}
        <div className="lg:col-span-1 space-y-6" data-id="element-1082">
          <div className="bg-white rounded-xl border border-gray-200 p-6" data-id="element-1083">
            <h2 className="font-semibold text-gray-900 mb-4" data-id="element-1084">
              Report Configuration
            </h2>
            <div className="space-y-4" data-id="element-1085">
              <div data-id="element-1086">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1087">
                  Report Type
                </label>
                <select value={reportType} onChange={e => {
                setReportType(e.target.value as ReportType);
                setShowPreview(false);
              }} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1088">
                  <option value="land-summary" data-id="element-1089">Land Record Summary</option>
                  <option value="district-wise" data-id="element-1090">
                    District-wise Distribution
                  </option>
                  <option value="deed-wise" data-id="element-1091">Deed Type Analysis</option>
                  <option value="litigation" data-id="element-1092">Litigation Summary</option>
                  <option value="cost-summary" data-id="element-1093">Cost Summary</option>
                </select>
              </div>

              <div data-id="element-1094">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1095">
                  Date Range
                </label>
                <div className="grid grid-cols-2 gap-2" data-id="element-1096">
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" data-id="element-1097" />
                  <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" data-id="element-1098" />
                </div>
              </div>

              <div data-id="element-1099">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1100">
                  District Filter
                </label>
                <select value={selectedDistrict} onChange={e => setSelectedDistrict(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1101">
                  <option value="" data-id="element-1102">All Districts</option>
                  {districts.map(d => <option key={d.id} value={d.id} data-id="element-1103">
                      {d.name}
                    </option>)}
                </select>
              </div>

              <button onClick={handleGenerate} disabled={isGenerating} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50" data-id="element-1104">
                {isGenerating ? <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-1105" />
                    Generating...
                  </> : <>
                    <BarChart3Icon className="w-5 h-5" data-id="element-1106" />
                    Generate Report
                  </>}
              </button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-id="element-1107">
            <div className="flex items-start gap-3" data-id="element-1108">
              <div className="p-2 bg-blue-100 rounded-lg" data-id="element-1109">
                <FileTextIcon className="w-5 h-5 text-blue-600" data-id="element-1110" />
              </div>
              <div data-id="element-1111">
                <h3 className="font-medium text-blue-900" data-id="element-1112">
                  Need a custom report?
                </h3>
                <p className="text-sm text-blue-700 mt-1" data-id="element-1113">
                  Contact the IT department to request specific data analysis or
                  custom report templates.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Area */}
        <div className="lg:col-span-2" data-id="element-1114">
          {showPreview ? <div className="bg-white rounded-xl border border-gray-200 flex flex-col h-full" data-id="element-1115">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-1116">
                <div data-id="element-1117">
                  <h2 className="font-semibold text-gray-900" data-id="element-1118">
                    {getReportTitle()}
                  </h2>
                  <p className="text-sm text-gray-500" data-id="element-1119">
                    Generated on {new Date().toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2" data-id="element-1120">
                  <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg" title="Print" data-id="element-1121">
                    <PrinterIcon className="w-5 h-5" data-id="element-1122" />
                  </button>
                  <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg" title="Email" data-id="element-1123">
                    <MailIcon className="w-5 h-5" data-id="element-1124" />
                  </button>
                  <button className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm" data-id="element-1125">
                    <DownloadIcon className="w-4 h-4" data-id="element-1126" />
                    Download PDF
                  </button>
                </div>
              </div>
              <div className="p-6 flex-1 overflow-auto" data-id="element-1127">
                <div className="mb-6 text-center" data-id="element-1128">
                  <h1 className="text-xl font-bold text-gray-900" data-id="element-1129">
                    Incepta Pharmaceuticals Ltd.
                  </h1>
                  <p className="text-gray-600" data-id="element-1130">Legal Affairs Department</p>
                  <div className="w-16 h-1 bg-blue-600 mx-auto mt-4 mb-2" data-id="element-1131" />
                  <h2 className="text-lg font-semibold text-gray-800" data-id="element-1132">
                    {getReportTitle()}
                  </h2>
                </div>
                {renderPreviewContent()}
              </div>
            </div> : <div className="bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 h-full flex flex-col items-center justify-center text-gray-400 p-8" data-id="element-1133">
              <BarChart3Icon className="w-16 h-16 mb-4 opacity-50" data-id="element-1134" />
              <p className="text-lg font-medium" data-id="element-1135">No report generated</p>
              <p className="text-sm mt-2" data-id="element-1136">
                Configure and generate a report to see the preview here
              </p>
            </div>}
        </div>
      </div>
    </div>;
}